/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Who draws the finding list on this surface** (`#1254`).
 *
 * A mapping editor does not know what is above it, and it should not have to: *is there a panel
 * listing these already* is one fact per **surface**, and the same answer for every editor on it.
 * Threading it as a prop would make fifteen call sites restate one page-level fact, and the failure
 * that produces is one call site disagreeing with its siblings on the same screen — which is worse
 * than the duplication being fixed. It is the same argument `ExpressionLibraryAccessContext` makes
 * beside it, and it is declared in the same shape for the same reason.
 *
 * **Absence means the editor draws them**, which is deliberate and is the safe direction: a surface
 * nobody has considered shows *more* rather than less, and the removal is opt-in by a page that has
 * taken the job on. The check that keeps it honest is not the default, it is a **count** —
 * `findings-render-once.test.tsx` asserts one list per surface. *Count them by finding, not by
 * control.*
 */

import React from 'react';

export type FindingsListOwner = 'editor' | 'page';

export const FindingsListOwnerContext = React.createContext<FindingsListOwner>('editor');

/** What draws the finding list here. See {@link FindingsListOwner}. */
export const useFindingsListOwner = (): FindingsListOwner => React.useContext(FindingsListOwnerContext);
